import type { Clip, Project } from '../types'
import { useEditor } from '../store/editor'
import { assetOf, clipEnd } from '../lib/model'
import { mediaFileUrl } from '../lib/api'
import { drawTextBlock, FONT_FAMILY, textBlock } from '../lib/textLayout'

// Preview engine: one hidden <video>/<audio>/<img> per media clip, kept in
// sync with a wall clock while playing, composited onto a canvas at project
// resolution. Geometry mirrors the server export (fit-inside-canvas × scale,
// centre + offset, rotation around centre) so preview == MP4.

type MediaEl = HTMLVideoElement | HTMLAudioElement | HTMLImageElement

interface Entry {
  el: MediaEl
  src: string
}

export interface ClipBounds { cx: number; cy: number; w: number; h: number; rotation: number }

const DRIFT = 0.25

function fade(c: Clip, t: number): number {
  const local = t - c.startSec
  let g = 1
  if (c.audio.fadeInSec > 0.01) g = Math.min(g, local / c.audio.fadeInSec)
  if (c.audio.fadeOutSec > 0.01) g = Math.min(g, (c.durationSec - local) / c.audio.fadeOutSec)
  return Math.max(0, Math.min(1, g))
}

function cssFilter(c: Clip): string {
  const k = c.color
  const parts: string[] = []
  const b = 1 + k.brightness * 0.6 + k.exposure * 0.5
  if (Math.abs(b - 1) > 0.001) parts.push(`brightness(${b.toFixed(3)})`)
  if (Math.abs(k.contrast) > 0.001) parts.push(`contrast(${(1 + k.contrast).toFixed(3)})`)
  if (Math.abs(k.saturation) > 0.001) parts.push(`saturate(${(1 + k.saturation).toFixed(3)})`)
  if (Math.abs(k.hue) > 0.1) parts.push(`hue-rotate(${k.hue.toFixed(1)}deg)`)
  if (k.temperature > 0.001) parts.push(`sepia(${(k.temperature * 0.35).toFixed(3)})`)
  return parts.length ? parts.join(' ') : 'none'
}

class Player {
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private entries = new Map<string, Entry>()
  private raf = 0
  private wasPlaying = false
  private base = 0
  private baseT = 0
  private lastSet = -1
  private dirty = true
  private lastKey = ''
  bounds = new Map<string, ClipBounds>()
  private listeners = new Set<() => void>()

  attach(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
    this.dirty = true
    if (!this.raf) this.raf = requestAnimationFrame(this.tick)
    for (const w of [400, 700, 800]) {
      document.fonts.load(`${w} 40px ${FONT_FAMILY}`).then(() => { this.dirty = true }).catch(() => {})
    }
    document.fonts.load(`italic 400 40px ${FONT_FAMILY}`).catch(() => {})
    document.fonts.load(`italic 700 40px ${FONT_FAMILY}`).catch(() => {})
  }

  detach() {
    cancelAnimationFrame(this.raf)
    this.raf = 0
    this.canvas = null
    this.ctx = null
    for (const e of this.entries.values()) this.release(e)
    this.entries.clear()
  }

  invalidate() { this.dirty = true }

  onDraw(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private release(e: Entry) {
    if (e.el instanceof HTMLMediaElement) {
      e.el.pause()
      e.el.removeAttribute('src')
      e.el.load()
    }
  }

  private entry(p: Project, c: Clip): Entry | null {
    const a = assetOf(p, c)
    if (!a) return null
    const src = mediaFileUrl(a.id)
    const cur = this.entries.get(c.id)
    if (cur && cur.src === src) return cur
    if (cur) this.release(cur)
    let el: MediaEl
    if (c.kind === 'image') {
      const img = new Image()
      img.onload = () => { this.dirty = true }
      img.src = src
      el = img
    } else {
      const m = c.kind === 'video' ? document.createElement('video') : document.createElement('audio')
      m.preload = 'auto'
      if (m instanceof HTMLVideoElement) m.playsInline = true
      m.addEventListener('seeked', () => { this.dirty = true })
      m.addEventListener('loadeddata', () => { this.dirty = true })
      m.src = src
      el = m
    }
    const e = { el, src }
    this.entries.set(c.id, e)
    return e
  }

  private tick = (now: number) => {
    this.raf = requestAnimationFrame(this.tick)
    const st = useEditor.getState()
    const p = st.project
    const dur = p.durationSec
    let t: number
    if (st.playing) {
      if (!this.wasPlaying) {
        this.base = now
        this.baseT = st.playhead >= dur - 0.02 ? 0 : st.playhead
      } else if (Math.abs(st.playhead - this.lastSet) > 1e-4) {
        // user seeked while playing
        this.base = now
        this.baseT = st.playhead
      }
      t = this.baseT + (now - this.base) / 1000
      if (t >= dur) {
        t = dur
        this.lastSet = t
        this.wasPlaying = false
        st.set({ playing: false, playhead: t })
      } else {
        this.lastSet = t
        this.wasPlaying = true
        st.setPlayhead(t)
      }
    } else {
      t = st.playhead
      this.wasPlaying = false
    }
    this.sync(p, t, st.playing && t < dur)
    const key = `${p.modifiedAt}|${t.toFixed(4)}|${this.canvas?.width}|${st.selection.join(',')}`
    if (st.playing || this.dirty || key !== this.lastKey) {
      this.lastKey = key
      this.dirty = false
      this.draw(p, t)
      for (const fn of this.listeners) fn()
    }
  }

  private sync(p: Project, t: number, playing: boolean) {
    const alive = new Set<string>()
    const trackById = new Map(p.tracks.map((tr) => [tr.id, tr]))
    for (const c of p.clips) {
      if (c.kind !== 'video' && c.kind !== 'audio' && c.kind !== 'image') continue
      alive.add(c.id)
      const e = this.entry(p, c)
      if (!e || !(e.el instanceof HTMLMediaElement)) continue
      const m = e.el
      const tr = trackById.get(c.trackId)
      const active = t >= c.startSec && t < clipEnd(c)
      const desired = c.sourceInSec + (t - c.startSec) * c.speed
      const silent = c.audio.muted || !!tr?.muted
      m.volume = silent ? 0 : Math.min(1, Math.max(0, c.audio.volume) * fade(c, t))
      m.muted = silent
      if (active && playing) {
        if (Math.abs(m.playbackRate - c.speed) > 1e-3) m.playbackRate = c.speed
        if (m.paused) {
          if (Math.abs(m.currentTime - desired) > 0.05) m.currentTime = desired
          m.play().catch(() => {})
        } else if (Math.abs(m.currentTime - desired) > DRIFT && !m.seeking) {
          m.currentTime = desired
        }
      } else {
        if (!m.paused) m.pause()
        if (active) {
          if (Math.abs(m.currentTime - desired) > 1 / 90 && !m.seeking) m.currentTime = desired
        } else if (playing && c.startSec > t && c.startSec - t < 1.5) {
          // warm up upcoming clip so it starts without a stall
          if (Math.abs(m.currentTime - c.sourceInSec) > 0.1 && !m.seeking) m.currentTime = c.sourceInSec
        }
      }
    }
    for (const [id, e] of this.entries) {
      if (!alive.has(id)) {
        this.release(e)
        this.entries.delete(id)
      }
    }
  }

  private draw(p: Project, t: number) {
    const cv = this.canvas
    const ctx = this.ctx
    if (!cv || !ctx) return
    const W = p.settings.width
    const H = p.settings.height
    if (cv.width !== W || cv.height !== H) {
      cv.width = W
      cv.height = H
    }
    this.renderFrame(ctx, p, t)
  }

  renderFrame(ctx: CanvasRenderingContext2D, p: Project, t: number) {
    const W = p.settings.width
    const H = p.settings.height
    ctx.save()
    ctx.setTransform(ctx.canvas.width / W, 0, 0, ctx.canvas.height / H, 0, 0)
    ctx.fillStyle = p.settings.backgroundColor || '#000'
    ctx.fillRect(0, 0, W, H)
    this.bounds.clear()
    const visual = p.tracks.filter((tr) => tr.kind === 'video' && tr.visible)
    const layers = [...visual].reverse() // bottom → top
    const active = (c: Clip) => t >= c.startSec && t < clipEnd(c)
    // media first (all layers), then texts — same order as the export graph
    for (const tr of layers) {
      for (const c of p.clips) {
        if (c.trackId !== tr.id || !active(c) || (c.kind !== 'video' && c.kind !== 'image')) continue
        this.drawMedia(ctx, p, c, t)
      }
    }
    for (const tr of layers) {
      for (const c of p.clips) {
        if (c.trackId !== tr.id || !active(c) || (c.kind !== 'text' && c.kind !== 'subtitle')) continue
        const b = textBlock(p, c)
        if (!b) continue
        const box = drawTextBlock(ctx, b)
        this.bounds.set(c.id, { cx: box.x + box.w / 2, cy: box.y + box.h / 2, w: box.w, h: box.h, rotation: 0 })
      }
    }
    ctx.restore()
  }

  private drawMedia(ctx: CanvasRenderingContext2D, p: Project, c: Clip, t: number) {
    const W = p.settings.width
    const H = p.settings.height
    const e = this.entries.get(c.id)
    let nw = 0
    let nh = 0
    let src: CanvasImageSource | null = null
    if (e?.el instanceof HTMLVideoElement) {
      nw = e.el.videoWidth; nh = e.el.videoHeight
      if (e.el.readyState >= 2) src = e.el
    } else if (e?.el instanceof HTMLImageElement) {
      nw = e.el.naturalWidth; nh = e.el.naturalHeight
      if (e.el.complete && nw) src = e.el
    }
    const a = assetOf(p, c)
    if (!nw || !nh) { nw = a?.info.width || W; nh = a?.info.height || H }
    const s = Math.max(0.01, c.transform.scale)
    const r = Math.min((W * s) / nw, (H * s) / nh)
    const w = nw * r
    const h = nh * r
    const cx = W / 2 + c.transform.x
    const cy = H / 2 + c.transform.y
    this.bounds.set(c.id, { cx, cy, w, h, rotation: c.transform.rotation })
    ctx.save()
    ctx.translate(cx, cy)
    if (c.transform.rotation) ctx.rotate((c.transform.rotation * Math.PI) / 180)
    ctx.globalAlpha = Math.max(0, Math.min(1, c.transform.opacity)) * fade(c, t)
    if (src) {
      ctx.filter = cssFilter(c)
      ctx.drawImage(src, -w / 2, -h / 2, w, h)
    } else {
      ctx.fillStyle = '#1b1b22'
      ctx.fillRect(-w / 2, -h / 2, w, h)
    }
    ctx.restore()
  }

  /** JPEG data URL of the current frame, for project thumbnails. */
  snapshot(width = 320): string | null {
    const st = useEditor.getState()
    const p = st.project
    const cv = document.createElement('canvas')
    cv.width = width
    cv.height = Math.round((width * p.settings.height) / p.settings.width)
    const ctx = cv.getContext('2d')
    if (!ctx) return null
    try {
      this.renderFrame(ctx, p, Math.min(st.playhead, Math.max(0, p.durationSec - 0.05)))
      return cv.toDataURL('image/jpeg', 0.72)
    } catch {
      return null
    }
  }
}

export const player = new Player()
